import { useEffect, useRef, useState } from 'react'
import * as tf from '@tensorflow/tfjs'
import * as speechCommands from '@tensorflow-models/speech-commands'
import { vadDetector } from './vadDetector'

/**
 * Custom hook that loads and runs the trained audio model.
 * Drop this into any run-stage component to get simultaneous audio inference.
 *
 * @param {object} params
 * @param {object} params.appModeStorage  - Redux appModeStorage slice
 * @param {object} params.worker          - BLE/serial worker used for USB TX
 * @param {object} params.allDetails      - IF-block details from sessionStorage
 * @param {boolean} params.isPlay         - True when the run stage is active
 * @param {object} params.enable          - Session enable flags (s1.mic, s1.pc…)
 * @param {function} params.getByID       - useIndexedDB('ProgramData').getByID
 * @param {function} params.onAction      - Optional callback called with matched block data
 * @param {function} params.onRecognize   - Optional callback (classId, className, confidence) on each detection
 */
const useAudioModel = ({
    appModeStorage,
    worker,
    allDetails,
    isPlay,
    enable,
    getByID,
    onAction,
    onRecognize,
}) => {
    const [audioResults, setAudioResults] = useState('')
    const [classPredictions, setClassPredictions] = useState({})
    const [audioClasses, setAudioClasses] = useState({})
    const [isAudioModelLoaded, setIsAudioModelLoaded] = useState(false)

    const [isSpeechCommandsModel, setIsSpeechCommandsModel] = useState(false)
    const [isMultiModel, setIsMultiModel] = useState(false)

    const recognizerRef = useRef(null)
    const customModelRef = useRef(null)
    const labelsRef = useRef([])
    const predictingRef = useRef(false)
    const predictionHistoryRef = useRef([])
    const audioBufferRef = useRef([])
    const pendingActionsRef = useRef([])
    const audioClassesRef = useRef({})
    const allDetailsRef = useRef(allDetails)
    const humanModelRef = useRef(null)
    const envModelRef = useRef(null)
    const humanLabelsRef = useRef([])
    const envLabelsRef = useRef([])
    const isMultiModelRef = useRef(false)
    const isSpeechCommandsModelRef = useRef(false)
    const SpeechRecognitionRef = useRef(null)
    const isLoadingRef = useRef(false)

    // Keep refs in sync
    useEffect(() => { allDetailsRef.current = allDetails }, [allDetails])
    useEffect(() => { audioClassesRef.current = audioClasses }, [audioClasses])
    useEffect(() => { isMultiModelRef.current = isMultiModel }, [isMultiModel])
    useEffect(() => { isSpeechCommandsModelRef.current = isSpeechCommandsModel }, [isSpeechCommandsModel])

    // ── STT helpers (for speakerSayThis feature) ─────────────────────────────
    const startSpeechToText = () => {
        if (SpeechRecognitionRef.current) return
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SR) return
        const rec = new SR()
        rec.continuous = true
        rec.interimResults = true
        rec.lang = 'en-US'
        rec.onresult = (e) => {
            let transcript = ''
            for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript
            if (pendingActionsRef.current.length > 0) {
                const lower = transcript.toLowerCase()
                const remaining = []
                for (const pending of pendingActionsRef.current) {
                    if (lower.includes(pending.triggerWord)) fireAction(pending.val)
                    else remaining.push(pending)
                }
                pendingActionsRef.current = remaining
            }
        }
        rec.onend = () => { SpeechRecognitionRef.current = null }
        rec.start()
        SpeechRecognitionRef.current = rec
    }

    const stopSpeechToText = () => {
        if (SpeechRecognitionRef.current) {
            SpeechRecognitionRef.current.stop()
            SpeechRecognitionRef.current = null
        }
    }

    // ── Action firing ─────────────────────────────────────────────────────────
    const fireAction = (val) => {
        if (onAction) { onAction(val); return }
        if (val.displayText && typeof val.displayText === 'string') {
            // Caller can subscribe via onAction; default is a no-op here
        }
        if (val.image && getByID) {
            getByID(val.id).then((event) => {
                if (event?.imageURL && onAction) onAction({ ...val, resolvedImage: event.imageURL })
            })
        }
        if (enable?.s1?.pc && val.sliderOption === 'TX1' && worker) {
            worker.postMessage({ type: 'writeArray', value: [85, 83, Number(val.sliderValue)] })
        }
    }

    const handleDetectedAction = (classId) => {
        const classIdStr = String(classId)
        Object.entries(allDetailsRef.current).forEach(([, val]) => {
            if (
                String(val.speechText) === classIdStr ||
                String(val.selectedObject) === classIdStr
            ) {
                const triggerWord = val.speakerSayThis?.trim()
                if (triggerWord) {
                    pendingActionsRef.current.push({ val, triggerWord: triggerWord.toLowerCase() })
                    startSpeechToText()
                } else {
                    fireAction(val)
                }
            }
        })
    }

    // ── Model loading ─────────────────────────────────────────────────────────
    const loadAudioModel = async () => {
        if (isLoadingRef.current) return
        try {
            isLoadingRef.current = true
            setAudioResults('Loading audio model…')

            const currentModelId = appModeStorage.trainMachine.currentModelId
            const modelMeta = appModeStorage.trainMachine.camModels.find(
                (m) => m.modelId === currentModelId
            )

            if (!modelMeta?.isGenerateTensor) {
                setAudioResults('No trained audio model found')
                return
            }

            const classesObj = modelMeta.AudioClasses || {}
            setAudioClasses(classesObj)
            audioClassesRef.current = classesObj

            const initialPreds = {}
            Object.entries(classesObj).forEach(([classKey, c]) => {
                if (c) initialPreds[classKey] = { className: c.className, confidence: 0 }
            })
            setClassPredictions(initialPreds)

            if (modelMeta.isSpeechCommandsModel) {
                const scLabels = modelMeta.speechCommandsLabels || []
                const baseRec = speechCommands.create('BROWSER_FFT')
                await baseRec.ensureModelLoaded()
                const transferRec = baseRec.createTransfer(`transfer-${currentModelId}`)
                // Try new _audio path first; fall back to legacy path for older trained models
                try {
                    await transferRec.load(`indexeddb://${currentModelId}_audio`)
                } catch (_) {
                    await transferRec.load(`indexeddb://${currentModelId}`)
                }
                transferRec.words = scLabels
                recognizerRef.current = transferRec
                customModelRef.current = transferRec
                labelsRef.current = scLabels
                setIsSpeechCommandsModel(true)
                setIsMultiModel(false)
            } else if (modelMeta.isMultiModel) {
                const hLabels = modelMeta.humanAudioLabels || []
                const eLabels = modelMeta.envAudioLabels || []
                const baseRec = speechCommands.create('BROWSER_FFT')
                await baseRec.ensureModelLoaded()
                recognizerRef.current = baseRec
                let hModel = null, eModel = null
                if (hLabels.length >= 1) {
                    try { hModel = await tf.loadLayersModel(`indexeddb://${currentModelId}_human`) }
                    catch (e) { console.warn('Human sub-model not found:', e.message) }
                }
                if (eLabels.length >= 2) {
                    try { eModel = await tf.loadLayersModel(`indexeddb://${currentModelId}_env`) }
                    catch (e) { console.warn('Env sub-model not found:', e.message) }
                }
                humanModelRef.current = hModel
                envModelRef.current = eModel
                humanLabelsRef.current = hLabels
                envLabelsRef.current = eLabels
                labelsRef.current = [...hLabels, ...eLabels]
                customModelRef.current = hModel || eModel
                setIsMultiModel(true)
                setIsSpeechCommandsModel(false)
                await vadDetector.initializeVAD()
            } else {
                const baseRec = speechCommands.create('BROWSER_FFT')
                await baseRec.ensureModelLoaded()
                recognizerRef.current = baseRec
                const loadedModel = await tf.loadLayersModel(`indexeddb://${currentModelId}_audio`)
                const audioLabels = modelMeta.audioLabels || []
                customModelRef.current = loadedModel
                labelsRef.current = audioLabels
                setIsMultiModel(false)
                setIsSpeechCommandsModel(false)
            }

            setAudioResults('Audio model ready')
            setIsAudioModelLoaded(true)
        } catch (err) {
            console.error('useAudioModel: failed to load', err)
            setAudioResults('Error loading audio model')
        } finally {
            isLoadingRef.current = false
        }
    }

    // ── Prediction loop ───────────────────────────────────────────────────────
    const startAudioPrediction = () => {
        if (!recognizerRef.current || !customModelRef.current) return
        if (predictingRef.current) return
        predictingRef.current = true
        predictionHistoryRef.current = []

        if (isSpeechCommandsModelRef.current) {
            recognizerRef.current.listen(
                async (result) => {
                    const scores = Array.from(result.scores)
                    const wordLabels = recognizerRef.current.wordLabels()
                    predictionHistoryRef.current.push(scores)
                    if (predictionHistoryRef.current.length > 2) predictionHistoryRef.current.shift()
                    const averaged = scores.map((_, i) => {
                        const sum = predictionHistoryRef.current.reduce((s, h) => s + h[i], 0)
                        return sum / predictionHistoryRef.current.length
                    })
                    let maxIndex = 0, maxProb = -1
                    averaged.forEach((p, i) => { if (p > maxProb) { maxProb = p; maxIndex = i } })
                    const detectedClassId = wordLabels[maxIndex]
                    const audioClassesToUse = audioClassesRef.current || {}
                    setClassPredictions((prev) => {
                        const next = { ...prev }
                        wordLabels.forEach((classId, idx) => {
                            const cd = audioClassesToUse[classId]
                            if (cd) next[classId] = { className: cd.className, confidence: averaged[idx] || 0 }
                        })
                        return next
                    })
                    if (maxProb > 0.85) {
                        const classInfo = audioClassesToUse[detectedClassId]
                        if (classInfo && classInfo.className !== 'Background_Noise') {
                            setAudioResults(classInfo.className)
                            handleDetectedAction(detectedClassId)
                            startSpeechToText()
                            if (onRecognize)
                                onRecognize(
                                    classInfo.id,
                                    classInfo.className,
                                    maxProb
                                )
                        }
                    }
                },
                { probabilityThreshold: 0, invokeCallbackOnNoiseAndUnknown: true, overlapFactor: 0.9 }
            )
        } else {
            recognizerRef.current.listen(
                async (result) => {
                    audioBufferRef.current.push(result.spectrogram.data)
                    if (audioBufferRef.current.length < 2) return
                    const combinedData = []
                    for (const chunk of audioBufferRef.current) for (let i = 0; i < chunk.length; i++) combinedData.push(chunk[i])
                    audioBufferRef.current = []
                    const normalizedData = combinedData.map((v) => (v + 100) / 100)
                    let modelForPrediction, labelsForPrediction, isVoice
                    if (isMultiModelRef.current) {
                        isVoice = vadDetector.isSpeechDetected()
                        modelForPrediction = isVoice ? humanModelRef.current : envModelRef.current
                        labelsForPrediction = isVoice ? humanLabelsRef.current : envLabelsRef.current
                        if (!modelForPrediction || !labelsForPrediction?.length) return
                    } else {
                        modelForPrediction = customModelRef.current
                        labelsForPrediction = labelsRef.current
                        if (!modelForPrediction) return
                    }
                    const tensor = tf.tidy(() => {
                        const t = tf.tensor(normalizedData, [1, 172, 232, 1])
                        const s = modelForPrediction.layers[0].batchInputShape
                        return s.length === 2 ? t.reshape([1, -1])
                            : s.length === 4 ? tf.image.resizeBilinear(t, [s[1], s[2]])
                            : t
                    })
                    try {
                        const pred = modelForPrediction.predict(tensor)
                        const probs = pred.dataSync()
                        pred.dispose()
                        predictionHistoryRef.current.push(probs)
                        if (predictionHistoryRef.current.length > 2) predictionHistoryRef.current.shift()
                        const averaged = new Float32Array(probs.length)
                        for (let i = 0; i < probs.length; i++) {
                            let sum = 0
                            for (const h of predictionHistoryRef.current) sum += h[i]
                            averaged[i] = sum / predictionHistoryRef.current.length
                        }
                        let maxIndex = 0, maxProb = -1
                        for (let i = 0; i < averaged.length; i++) {
                            if (averaged[i] > maxProb) { maxProb = averaged[i]; maxIndex = i }
                        }
                        if (maxIndex < 0 || maxIndex >= labelsForPrediction.length) return
                        const detectedClassId = labelsForPrediction[maxIndex]
                        if (detectedClassId === undefined) return
                        const audioClassesToUse = audioClassesRef.current || {}
                        const classInfo = Object.values(audioClassesToUse).find(
                            (c) => c && Number(c.id) === Number(detectedClassId)
                        )
                        setClassPredictions((prev) => {
                            const next = { ...prev }
                            labelsForPrediction.forEach((classId, idx) => {
                                const cd = Object.values(audioClassesToUse).find(
                                    (c) => c && Number(c.id) === Number(classId)
                                )
                                if (cd) next[classId] = { className: cd.className, confidence: averaged[idx] || 0 }
                            })
                            const otherLabels = isVoice ? envLabelsRef.current : humanLabelsRef.current
                            otherLabels.forEach((id) => { if (next[id]) next[id].confidence = 0 })
                            return next
                        })
                        if (maxProb > 0.85 && classInfo && classInfo.className !== 'Background_Noise') {
                            setAudioResults(classInfo.className)
                            handleDetectedAction(detectedClassId)
                            startSpeechToText()
                            if (onRecognize)
                                onRecognize(
                                    Number(classInfo.id),
                                    classInfo.className,
                                    maxProb
                                )
                        }
                    } finally {
                        tensor.dispose()
                    }
                },
                { includeSpectrogram: true, probabilityThreshold: 0.5, overlapFactor: 0.9 }
            )
        }
        if (isMultiModelRef.current) vadDetector.startVAD()
    }

    const stopAudioPrediction = () => {
        if (recognizerRef.current && predictingRef.current) {
            try {
                const result = recognizerRef.current.stopListening()
                if (result && typeof result.catch === 'function') {
                    result.catch(() => {})
                }
            } catch (_) {}
        }
        predictingRef.current = false
        audioBufferRef.current = []
        pendingActionsRef.current = []
        if (isMultiModelRef.current) {
            try { vadDetector.stopVAD() } catch (_) {}
        }
        stopSpeechToText()
        setAudioResults('Stopped')
    }

    // ── Load on mount (only when mic was enabled) ─────────────────────────────
    useEffect(() => {
        if (enable?.s1?.mic) loadAudioModel()
        return () => { stopAudioPrediction() }
    }, [])

    // ── Start / stop in sync with isPlay ──────────────────────────────────────
    useEffect(() => {
        if (!enable?.s1?.mic || !isAudioModelLoaded) return
        if (isPlay) {
            startAudioPrediction()
            setAudioResults('listening…')
        } else if (predictingRef.current) {
            stopAudioPrediction()
        }
    }, [isPlay, isAudioModelLoaded])

    return { classPredictions, audioResults, audioClasses, isAudioModelLoaded }
}

export default useAudioModel
